import React, { useCallback, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { Swiper, SwiperSlide } from 'swiper/react';
import { buildClassName } from 'utils/build-class-name';
import ListingImage from 'components/listing-image';
import CarouselArrows from 'components/swiper-carousel/components/arrows';
import ShareSaveListing from 'components/swiper-carousel/components/share-save-listing';
import NoImage from 'components/image-loader/image/no-image';
import { listingPageModalIds, blurIds } from 'constants/test-constants';
import styles from './style.module.scss';
import { GTM_CLICK_ADDRESS_PAGE_IMAGE_RIGHT_ARROW } from 'constants/events';
import { useIsMobile } from 'hooks/use-size-class';
import { useThemeContext } from 'contexts/theme';
import { Trans, useTranslation } from 'next-i18next';
import LoadingSkeleton from 'components/loading-skeleton';
import { isExpTenant } from '@tenants';

import type SwiperClass from 'swiper/types/swiper-class';
import type { Point } from 'data/listing';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { ComplianceRequirement } from 'types/compliance';
import { AnalyticsController } from 'analytics/analytics-controller';
import { Provider } from '@zoocasa/go-search';

// Define dynamic map component outside the component
const DynamicMap = dynamic(() => import('components/map/listing'), {
  loading: () => <LoadingSkeleton height="100%" width="100%" />,
  ssr: false,
});

const MOBILE_INDEX_LOAD_START = 1 as const;
const DESKTOP_INDEX_LOAD_START = 3 as const;

export type SwiperCarouselProps = {
  listingData: {
    id: number;
    imageUrls: string[];
    imageDescs: Map<string, string> | null;
    position: Point | null;
    initialSlide?: number;
    setActiveIndex?: (index: number) => void;
    isLightbox?: boolean;
    onImageClick?: (index: number | 'map') => void;
  };
  share?: {
    text: string;
    url: string;
  };
  complianceRequirement?: ComplianceRequirement;
  onComplianceRequirementClick?: (complianceRequirement: ComplianceRequirement, providerId: number) => void;
  imageAltFallback: string;
  usePriorityMode?: boolean;
  isModal?: boolean;
  showFavouriteToggle?: boolean;
  // Element visibility (controlled by parent/orchestrator)
  showShareButton?: boolean;
  showLikeButton?: boolean;
  /** Optional overlay (e.g. Coming Soon badge) rendered top-left over the carousel */
  badgesSlot?: React.ReactNode;
};

//#region sub-components
function RequireSignIn({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <div className={styles['sign-in-overlay']} onClick={onClick} data-testid={blurIds.blurredListingImageCarousel}>
      <div className={styles.cta} data-testid={blurIds.overlaySignInMsg}>
        <Trans
          t={t}
          i18nKey={LOCALE_COMMON_PATHS.COMPLIANCE_SWIPER_CAROUSEL_SIGN_IN_REQUIRED}
          components={{ span: <span data-testid={blurIds.overlaySignInBtn} /> }}
        />
      </div>
    </div>
  );
}

function RequireTermsOfUse({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <div className={styles['sign-in-overlay']} onClick={onClick} data-testid={blurIds.blurredListingImageCarousel}>
      <div className={styles.cta} data-testid={blurIds.overlayAcceptTermsBtn}>
        <Trans
          t={t}
          i18nKey={LOCALE_COMMON_PATHS.COMPLIANCE_SWIPER_CAROUSEL_TERMS_OF_USE_REQUIRED}
          components={{ span: <span data-testid={blurIds.overlayAcceptTermsMsg} /> }}
        />
      </div>
    </div>
  );
}

function ComplianceRequirementOverlay({ complianceRequirement, onClick }: { complianceRequirement: ComplianceRequirement; onClick: () => void }) {
  if (complianceRequirement === ComplianceRequirement.SIGN_IN) {
    return <RequireSignIn onClick={onClick} />;
  } else if (complianceRequirement === ComplianceRequirement.TERMS_OF_USE) {
    return <RequireTermsOfUse onClick={onClick} />;
  }
  return null;
}
//#endregion

/**
 * @deprecated This component is too coupled with the Listing data model and contains complex
 * authentication, privacy, and real estate-specific logic. Please consider developing new functionality or features
 * in a modular way or by adding to ImageCarousel (src/components/image-carousel/ImageCarousel.tsx).
 */
export default function SwiperCarousel(props: SwiperCarouselProps) {
  const {
    listingData,
    usePriorityMode,
    isModal = false,
    showFavouriteToggle = true,
    showShareButton = true, // Default to true for backward compatibility
    showLikeButton = true,   // Default to true for backward compatibility
    imageAltFallback,
    complianceRequirement,
    onComplianceRequirementClick,
    share,
    badgesSlot,
  }: SwiperCarouselProps = props;
  const { imageUrls, imageDescs, position, initialSlide, setActiveIndex, isLightbox, id, onImageClick } = listingData;
  const [swiper, setSwiper] = useState<SwiperClass>();
  const [activeSlide, setActiveSlide] = useState(initialSlide || 0);
  const [carouselItems, setCarouselItems] = useState<JSX.Element[]>([]);
  const [hasLoadedInitialItems, setHasLoadedInitialItems] = useState(false);
  const [hasLoadedAllItems, setHasLoadedAllItems] = useState(false);
  // const isPrivate = !isAuthenticated && listing.isPrivate;
  // const imageAlt = getListingImageAlt(listing);
  const isAddressPageHeader = !isLightbox && !isModal;
  const shouldShowSignInWall = complianceRequirement === ComplianceRequirement.SIGN_IN;
  const shouldShowTermsWall = complianceRequirement === ComplianceRequirement.TERMS_OF_USE;
  const shouldBlur = shouldShowSignInWall || shouldShowTermsWall;
  const isMobile = useIsMobile();
  const { tenantId } = useThemeContext();
  const isExpTheme = isExpTenant(tenantId);
  const isMobileLightbox = isMobile && isLightbox;
  const hasCoordinates = position?.coordinates?.length === 2;

  const carouselImages = imageUrls.map(
    (image, index) => ({
      image,
      index,
      id: `${index}-carousel-${id}`,
      alt: imageDescs && imageDescs.has(index.toString()) ?
        `${imageDescs.get(index.toString())} | Image ${index + 1}` : `${imageAltFallback} | Image ${index + 1}`,
      imageDesc: (imageDescs && imageDescs.has(index.toString())) ?
        imageDescs.get(index.toString()) : '',
    }));

  const loadInitialCarouselItems = useCallback(() => { // Only for desktop, skip for lightbox
    if (!isMobile && !hasLoadedInitialItems && !isLightbox) {
      const items = (carouselImages.slice(1, DESKTOP_INDEX_LOAD_START) || []).map((item, index) => (
        <SwiperSlide key={index + 1} className={buildClassName(styles['carousel-item'])}>
          <ListingImage
            item={item}
            onClick={() => onImageClick?.(item.index)}
            isLightbox={false}
            usePriorityMode={index < 4 && usePriorityMode}
            isVisible={index <= 4}
            loadLowQualityFirst={!isModal}
          />
        </SwiperSlide>
      ));

      if (carouselImages.length < DESKTOP_INDEX_LOAD_START && position && !!carouselImages?.length && hasCoordinates) { // if not enough images, show map in initial items
        items.push(
          <SwiperSlide key="map-slide" className={buildClassName(styles['carousel-item'], styles['no-image'])}>
            <span onClick={() => onImageClick?.('map')}>
              <DynamicMap
                listingId={id}
                position={position}
                className={styles.map}
                isInteractive={false}
              />
            </span>
          </SwiperSlide>
        );
      }
      setCarouselItems(items);
      setHasLoadedInitialItems(true);
    }
  }, [carouselImages, hasCoordinates, hasLoadedInitialItems, id, usePriorityMode, isLightbox, isMobile, isModal, onImageClick, position]);

  const loadRestOfItems = useCallback(() => {
    if (!hasLoadedAllItems && !!carouselImages?.length) {
      // In lightbox mode, load all items from index 0 since we don't render firstImage separately
      const INDEX_LOAD_START = isLightbox ? 0 : (isMobile ? MOBILE_INDEX_LOAD_START : DESKTOP_INDEX_LOAD_START);
      const newItems = (carouselImages.slice(INDEX_LOAD_START) || []).map((item, index) => (
        <SwiperSlide key={isLightbox ? index : (isMobile ? index + MOBILE_INDEX_LOAD_START : index + DESKTOP_INDEX_LOAD_START)} className={buildClassName(styles['carousel-item'], isLightbox && styles.full)}>
          <ListingImage
            item={item}
            onClick={() => onImageClick?.(item.index)}
            isLightbox={isLightbox}
            usePriorityMode={index < 4 && usePriorityMode}
            isVisible={isMobile && index <= 1 ? true : !isMobile && index <= 4}
          />
        </SwiperSlide>
      ));

      // Add map for mobile now or if it wasn't added in the initial load for desktop
      if ((isMobile || carouselImages.length >= 3) && hasCoordinates && !isLightbox) {
        newItems.push(
          <SwiperSlide key="map-slide" className={buildClassName(styles['carousel-item'], isLightbox && styles.full, styles['no-image'])}>
            <span onClick={() => onImageClick?.('map')}>
              <DynamicMap
                listingId={id}
                position={position}
                className={styles.map}
                isInteractive={false}
              />
            </span>
          </SwiperSlide>
        );
      }

      setCarouselItems([...carouselItems, ...newItems]);
      setHasLoadedAllItems(true);
    }
  }, [carouselImages, carouselItems, hasCoordinates, hasLoadedAllItems, id, usePriorityMode, isLightbox, isMobile, onImageClick, position]);

  // Initialize carousel items on mount
  useEffect(() => {
    if (!hasLoadedInitialItems && carouselImages.length > 0) {
      if (isLightbox) {
        // For lightbox, skip initial loading and go straight to loading all items
        // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing, unrelated to Coming Soon changes
        setHasLoadedInitialItems(true);
      } else {
        loadInitialCarouselItems();
      }
    }
  }, [hasLoadedInitialItems, carouselImages.length, loadInitialCarouselItems, isLightbox]);

  // Load remaining items when needed
  useEffect(() => {
    if (!hasLoadedAllItems && hasLoadedInitialItems && carouselImages.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing, unrelated to Coming Soon changes
      loadRestOfItems();
    }
  }, [hasLoadedAllItems, hasLoadedInitialItems, carouselImages.length, loadRestOfItems]);


  const buildFirstImage = useCallback(() => {
    if (carouselImages?.length) {
      const initialImageIndex = initialSlide || 0;
      return (
        <SwiperSlide key={initialImageIndex} className={buildClassName(styles['carousel-item'], isLightbox && styles.full)}>
          <ListingImage
            item={carouselImages[initialImageIndex]}
            onClick={() => onImageClick?.(initialImageIndex)}
            isVisible={true}
            isLightbox={isLightbox}
            usePriorityMode={usePriorityMode}
            loadLowQualityFirst={!isModal}
          />
        </SwiperSlide>
      );
    } else {
      return (
        <SwiperSlide key={0} className={buildClassName(styles['carousel-item'], isLightbox && styles.full, styles['no-image'])}>
          <NoImage isExpTheme={isExpTheme} />
        </SwiperSlide>
      );
    }
  }, [carouselImages, initialSlide, usePriorityMode, isExpTheme, isLightbox, isModal, onImageClick]);

  const firstImage = buildFirstImage();

  const slideTo = (slide: 'next' | 'prev' | number) => {
    let targetSlide = null;
    if (slide === 'next') {
      targetSlide = activeSlide < carouselItems.length - 1 ? activeSlide + 1 : 0;
      AnalyticsController.trackEventConditionally({ event: GTM_CLICK_ADDRESS_PAGE_IMAGE_RIGHT_ARROW }, !isModal);
    } else if (slide === 'prev') {
      targetSlide = activeSlide > 0 ? activeSlide - 1 : carouselItems.length - 1;
    } else {
      targetSlide = slide;
    }

    swiper?.slideTo(targetSlide);
    setActiveSlide(targetSlide);
  };

  useEffect(() => {
    setActiveIndex?.(activeSlide);
  }, [activeSlide, setActiveIndex]);

  if (isMobileLightbox && !hasLoadedAllItems) {
    loadRestOfItems();
  }

  return (
    <div className={buildClassName(styles.component, styles.margin)} data-testid={listingPageModalIds.listingImageCarousel} onMouseOver={!isLightbox ? loadRestOfItems : undefined}>
      <Swiper
        onSwiper={setSwiper}
        spaceBetween={0}
        slidesPerView={1}
        initialSlide={initialSlide}
        onSlideChangeTransitionStart={() => !isLightbox && loadRestOfItems}
        onTouchStart={() => !isLightbox && loadRestOfItems}
        onSlideChange={(swiper: SwiperClass) => {
          setActiveSlide(swiper.realIndex);
          if (!hasLoadedAllItems && !isLightbox) {
            loadRestOfItems();
          }
        }}
        breakpoints={{ 768: { slidesPerView: 'auto' }}}
        className={buildClassName(styles.slider, shouldBlur && styles['blur-image'], isAddressPageHeader && styles.header)}
        data-testid={listingPageModalIds.swiperCarouselComponent}
      >
        {!isLightbox && firstImage}
        {carouselItems}
      </Swiper>
      {!isLightbox && badgesSlot}
      <ComplianceRequirementOverlay
        complianceRequirement={complianceRequirement}
        onClick={() => onComplianceRequirementClick?.(complianceRequirement, Provider.PROVIDER_UNSPECIFIED)}
      />
      {!isMobileLightbox && !!carouselImages?.length &&
        <CarouselArrows
          isLightbox={isLightbox}
          isListingCardCarousel={false}
          slideTo={slideTo}
          className={styles.arrow}
          disablePrev={activeSlide === 0}
          disableNext={activeSlide === carouselImages.length - 1} />}
      {!isLightbox && share && (showShareButton || showLikeButton) &&
        <ShareSaveListing
          showFavouriteToggle={showFavouriteToggle && showLikeButton}
          showShareButton={showShareButton}
          id={id}
          text={share.text}
          url={share.url}
          isModal={isModal}
          tenantId={tenantId}
        />
      }
    </div>
  );
}
